import { Button, Icons, useToggle } from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useState } from 'react';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { CorporationUploadFileModal } from '../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { useServiceRequestFile } from '../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { updateTaskStatus, viewUploadedFile } from '../../utils';

export const BankWireInstructionsUpload = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const uploadModalVisibility = useToggle();
  const [file, setFile] = useState<FileBasic>();

  const [uploadedFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.BankWireInstructions,
    'corporation',
    task.id
  );

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadModalVisibility.show}
                variant="primary"
              >
                Upload <Icons.Upload01 className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload bank wire instructions (PDF)" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: uploadedFile?.id ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  if (uploadedFile?.id) await viewUploadedFile(uploadedFile.id);
                }}
              >
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Bank wire instructions uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                Upload <Icons.Upload01 className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload bank wire instructions (PDF)" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads bank wire instructions (PDF)" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: uploadedFile?.id ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  if (uploadedFile?.id) await viewUploadedFile(uploadedFile.id);
                }}
              >
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Bank wire instructions uploaded by client"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads bank wire instructions (PDF)" />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading="Upload bank wire instructions"
        subheading="Upload the bank wire instructions (sometimes called 'Account Information Sheet' or 'Wire Details Document') issued by your bank after account opening. This is usually a two-page PDF containing your account number, routing/ABA/SWIFT codes, and bank address."
        documentType={LegacyDocumentType.BankWireInstructions}
        onUploadSuccess={() =>
          updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          })
        }
      />
    </div>
  );
};
